import React from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { AppUser, UserRole, CommissionTier } from '../../../types';
import { motion } from 'motion/react';
import { PricingCommissionCards } from './PricingCommissionCards';
import { BrokerCommissionTiersTable } from './BrokerCommissionTiersTable';
import { PropertySalePriceCard } from './PropertySalePriceCard';
import { combineInitialAndExtraTiers } from '../../../utils/commissionTierUtils';

interface PropertyPricingCardProps {
  precio: number;
  admin: number;
  rate: number;
  value: number;
  appUser: AppUser | null;
  canReferCredit?: boolean;
  commissionTiers?: CommissionTier[] | null;
}

export const PropertyPricingCard: React.FC<PropertyPricingCardProps> = ({
  precio,
  admin,
  rate,
  value,
  appUser,
  canReferCredit,
  commissionTiers,
}) => {
  const isBroker = Boolean(
    appUser && [
      UserRole.BROKER,
      UserRole.BROKER_PREMIUM,
      UserRole.BROKER_INTERNO,
      UserRole.BROKER_EXTERIOR,
      UserRole.BROKERS_LEADS,
    ].includes(appUser.role as any)
  );

  const showCommission = Boolean(appUser && appUser.role !== UserRole.CLIENTE);
  const showCreditCommission = Boolean(showCommission && canReferCredit);
  const creditCommissionValue = precio * 0.004;
  const totalCommission = value + creditCommissionValue;

  const canMerge = showCommission && showCreditCommission;
  const [isMerged, setIsMerged] = React.useState(false);
  const [hasInteracted, setHasInteracted] = React.useState(false);

  const allTiers = React.useMemo(() => {
    if (!isBroker) return [];
    return combineInitialAndExtraTiers(precio, rate, value, commissionTiers);
  }, [isBroker, precio, rate, value, commissionTiers]);

  const [activeTierIdx, setActiveTierIdx] = React.useState(0);

  // Auto-cycle through tiers only when multiple options exist
  React.useEffect(() => {
    if (!isBroker || allTiers.length <= 1) return;
    const timer = setInterval(() => {
      setActiveTierIdx((prev) => (prev + 1) % allTiers.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [isBroker, allTiers.length]);

  const activeTier = isBroker && allTiers.length > 0
    ? allTiers[activeTierIdx % allTiers.length]
    : null;

  React.useEffect(() => {
    if (!canMerge || hasInteracted) return;
    const timer = setTimeout(() => {
      setIsMerged(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, [canMerge, hasInteracted]);

  const handleToggleMerge = () => {
    if (!canMerge) return;
    setHasInteracted(true);
    setIsMerged((prev) => !prev);
  };

  return (
    <div
      onClick={handleToggleMerge}
      className={`p-4 sm:p-6 rounded-2xl sm:rounded-3xl bg-slate-50 border border-slate-200/60 shadow-xl space-y-3.5 sm:space-y-4 animate-fade-in self-start w-full select-none transition-all duration-300 ${
        canMerge ? 'cursor-pointer hover:bg-slate-100/80 hover:shadow-2xl active:scale-[0.995]' : ''
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-xs sm:text-sm font-black uppercase tracking-[0.25em] text-slate-500">
          Desglose Financiero
        </h4>
        {canMerge && (
          <span className="text-[10px] sm:text-xs font-bold text-sky-600 bg-sky-100/90 px-2.5 py-1 rounded-md animate-pulse">
            {isMerged ? '⚡ Ver Desglose' : '⚡ Unir Comisiones'}
          </span>
        )}
      </div>

      {/* CARD 1: Precio de Venta (dinámico únicamente para Brokers) */}
      <PropertySalePriceCard basePrice={precio} activeTier={activeTier} />

      {/* Animated Commission Cards */}
      <PricingCommissionCards
        isMerged={Boolean(canMerge && isMerged)}
        rate={rate}
        value={value}
        creditCommissionValue={creditCommissionValue}
        totalCommission={totalCommission}
        showCommission={showCommission}
        showCreditCommission={showCreditCommission}
        activeTier={activeTier}
      />

      {/* Breakdown table of initial + extra tiers (exclusivo para brokers) */}
      {isBroker && allTiers.length > 1 && (
        <BrokerCommissionTiersTable
          tiers={allTiers}
          activeIdx={activeTierIdx}
          onSelectTier={(idx) => setActiveTierIdx(idx)}
        />
      )}

      {/* CARD: Administración */}
      {admin > 0 && (
        <motion.div
          layout
          className="bg-white p-4 rounded-2xl border border-slate-200/50 shadow-sm transform hover:scale-[1.01] transition-all"
        >
          <p className="text-[11px] sm:text-xs font-black uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">
            Valor de Administración
          </p>
          <p className="text-lg font-bold text-slate-700 break-all">{formatCurrency(admin)}</p>
        </motion.div>
      )}
    </div>
  );
};
